export type AgamiLibraryStatus = 'published' | 'drafts' | 'all'
export type AgamiLibrarySort = 'default' | 'name' | 'lastUpdate'

export interface AgamiLibraryQuery {
  status: AgamiLibraryStatus
  search: string
  activationType: number | null
  mediaType: number | null
  language: string | null
  sort: AgamiLibrarySort
  page: number
}

export const DEFAULT_AGAMI_LIBRARY_QUERY: AgamiLibraryQuery = {
  status: 'all',
  search: '',
  activationType: null,
  mediaType: null,
  language: null,
  sort: 'default',
  page: 1,
}

const STATUSES: AgamiLibraryStatus[] = ['published', 'drafts', 'all']
const SORTS: AgamiLibrarySort[] = ['default', 'name', 'lastUpdate']
const ACTIVATION_TYPES = [1, 2, 4]
const MEDIA_TYPES = [1, 2, 3]

const parseAllowedNumber = (value: string | null, allowed: number[]): number | null => {
  if (value === null) return null
  const parsed = Number(value)
  return allowed.includes(parsed) ? parsed : null
}

export const parseAgamiLibraryQuery = (params: URLSearchParams): AgamiLibraryQuery => {
  const status = params.get('status')
  const sort = params.get('sort')
  const language = params.get('language')
  const parsedPage = Number(params.get('page'))

  return {
    status: STATUSES.includes(status as AgamiLibraryStatus) ? (status as AgamiLibraryStatus) : 'all',
    search: params.get('q')?.trim() ?? '',
    activationType: parseAllowedNumber(params.get('activation'), ACTIVATION_TYPES),
    mediaType: parseAllowedNumber(params.get('media'), MEDIA_TYPES),
    language: language && (language === 'undeclared' || /^[a-z]{2}$/.test(language)) ? language : null,
    sort: SORTS.includes(sort as AgamiLibrarySort) ? (sort as AgamiLibrarySort) : 'default',
    page: Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1,
  }
}

const setOrDelete = (params: URLSearchParams, key: string, value: string | null, defaultValue?: string) => {
  if (value === null || value === '' || value === defaultValue) params.delete(key)
  else params.set(key, value)
}

export const updateAgamiLibraryQuery = (
  current: URLSearchParams,
  changes: Partial<AgamiLibraryQuery>
): URLSearchParams => {
  const next = new URLSearchParams(current)

  if ('status' in changes) setOrDelete(next, 'status', changes.status ?? null, 'all')
  if ('search' in changes) setOrDelete(next, 'q', changes.search?.trim() ?? null)
  if ('activationType' in changes) {
    setOrDelete(next, 'activation', changes.activationType === null ? null : String(changes.activationType))
  }
  if ('mediaType' in changes) {
    setOrDelete(next, 'media', changes.mediaType === null ? null : String(changes.mediaType))
  }
  if ('language' in changes) setOrDelete(next, 'language', changes.language ?? null)
  if ('sort' in changes) setOrDelete(next, 'sort', changes.sort ?? null, 'default')
  if ('page' in changes) setOrDelete(next, 'page', String(changes.page ?? 1), '1')

  const changesOnlyPage = Object.keys(changes).every((key) => key === 'page')
  if (!changesOnlyPage) next.delete('page')

  return next
}
